import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterLink, UrlTree } from '@angular/router';

import { resolveValue } from '@lvflanders/bar';

import { _BarControlsHelper } from '../../../../shared/bar-controls.helper';
import { BarLayoutService } from '../../bar-layout.service';
import { BarMenuItemOptions } from '../bar-menu-item-options.model';
import { BarMenuItemTag } from '../bar-menu-item-tag.model';

const CLASS_SELECTED_ITEM = 'bar-menu-link--selected';

@Component({
    selector: 'bar-layout-menu-list',
    templateUrl: './bar-layout-menu-list.component.html',
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, NgTemplateOutlet, RouterLink, MatTooltipModule, MatButtonModule, MatIconModule, AsyncPipe]
})
export class BarLayoutMenuListComponent {
    readonly controlsHelper = inject(_BarControlsHelper);
    readonly #router = inject(Router);
    readonly #sanitizer = inject(DomSanitizer);
    readonly #layout = inject(BarLayoutService);

    readonly menuLevel = input(0);
    readonly path = input('');
    @Input() items: BarMenuItemOptions[] = [];
    readonly parentExpanded = input(true);

    menuItemClicked(event, item, index) {
        if (!this.getItemProp(item, 'disabled')) {
            // auto close menu when in overlay modus and menu item has a link
            if (this.#layout.menuState$.getValue() === 'overlay' && (item.routerLink || item.routerUrl)) this.#layout.menuState$.next('closed');

            // if menu item has children, trigger expansion logic
            if (item.children && item.children.length) this.menuItemToggleClicked(event, index);
            if (item.action) item.action();
            if (item.routerUrl) {
                // when routerUrl is configured we want to use SPA navigation not href navigation
                event.preventDefault();
                this.#router.navigateByUrl(resolveValue(item.routerUrl)).then((val: boolean) => {
                    if (val) window.scrollTo(0, 0);
                });
            }
        } else return false;
    }

    menuItemToggleClicked(event, index) {
        event.stopPropagation();

        const tempItem = Object.assign(this.items[index]);
        if (!tempItem.disabled || !resolveValue(tempItem.disabled)) {
            tempItem.expanded = !tempItem.expanded;

            this.items = this.items.map((mapItem, mapIndex) => {
                if (mapIndex === index) return Object.assign(mapItem, tempItem);
                return mapItem;
            });
        }
    }

    getItemProp(item: BarMenuItemOptions, property) {
        if (item[property])
            if (property === 'href') return this.#sanitizer.bypassSecurityTrustUrl(resolveValue(item[property]));
            else return resolveValue(item[property]);
        else if (property === 'routerLink') return '/';
        else if (property === 'href' && item.href == null && item.routerUrl != null)
            // in case the href property is retrieved but we don't have an actual href we set the routerUrl as the href
            return resolveValue(item.routerUrl);
        else return null;
    }

    getPath(index) {
        const path = this.path();
        return path.length ? `${path}.${index}` : `${index}`;
    }

    getLinkClasses(item: BarMenuItemOptions, index: number): string {
        const menuLevelClasses = this.menuLevel() < 3 ? 'bar-menu-link--level-' + this.menuLevel() : 'bar-menu-link--level-deep';
        let activeItemClasses = '';

        let url: UrlTree = null;

        if (item.routerLink) {
            const link = resolveValue(item.routerLink);
            url = this.#router.createUrlTree(link);
        } else if (item.routerUrl) {
            const tempUrl = resolveValue(item.routerUrl);
            if (tempUrl != null) {
                url = this.#router.parseUrl(tempUrl);
            }
        }
        if (url != null) {
            // pathMatch check wether or not to match full path isActive's "exact" property, default true

            // map UrlTree to string
            const serializedUrl = this.#router.serializeUrl(url);
            // remove queryparams and fragment as it is not needed for url check
            const currentUrl = this.#router.url.split('?')[0].split('#')[0];
            // when pathmatch is prefix we check if the menu item is part of the current url (the start of)
            const active = item.pathMatch && item.pathMatch === 'prefix' ? currentUrl.indexOf(serializedUrl) === 0 : serializedUrl === currentUrl;

            if (active) activeItemClasses = CLASS_SELECTED_ITEM;
        }

        // if the consumer marked the menu item as selected we mark it as selected
        if (item.selected === true) activeItemClasses = CLASS_SELECTED_ITEM;
        // if the consumer explicitly marked it as not selected, make sure it doesn't appear as selected (because of url match)
        else if (item.selected === false && activeItemClasses) activeItemClasses = undefined;

        const clickableClasses = item.href || item.routerLink || item.routerUrl || item.action ? 'bar-menu-link--clickable' : '';
        return `${menuLevelClasses} ${activeItemClasses} ${clickableClasses}`;
    }

    getTagClasses(tag: BarMenuItemTag) {
        const classes: string[] = [];
        if (tag.class) classes.push(tag.class);
        classes.push(`bar-menu-link_tag--${tag.type || 'no-type'}`);
        return classes;
    }

    hasLink(item: BarMenuItemOptions): boolean {
        return resolveValue(item['disabled']) ? false : resolveValue(item.routerLink) || this.hasDefaultLink(item);
    }

    hasDefaultLink(item: BarMenuItemOptions): boolean {
        return resolveValue(item.href) || resolveValue(item.routerUrl);
    }
}
